import { Dexie, Table } from 'dexie';
import { DbCoaterInspectionInterface, DbReport, DbReportEntry } from '@workspace-app/shared';

export interface SyncQueueItem {
  id?: string;
  type: 'report-entry' | 'image' | 'document' | string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH';
  payload: unknown;
  metadata: {
    reportId?: string;
    inspectionId?: string;
    size?: number;
    mimeType?: string;
    [key: string]: unknown;
  };
  priority: number;
  status: 'pending' | 'syncing' | 'completed' | 'failed' | 'cancelled';
  retryCount: number;
  maxRetries: number;
  createdAt: Date;
  lastAttemptAt?: Date;
  completedAt?: Date;
  error?: string;
  // Progress tracking fields
  uploadProgress?: number; // Percentage 0-100
  uploadSpeed?: number; // Bytes per second
  uploadedBytes?: number; // Bytes uploaded so far
  totalBytes?: number; // Total bytes to upload
  uploadStartTime?: Date; // When upload started
  uploadEta?: number; // Estimated seconds remaining
}

export class AppDB extends Dexie {
  coatingInspections!: Table<DbCoaterInspectionInterface>;
  coatingInspectionReports!: Table<DbReport>;
  coatingLaboratoryReports!: Table<DbReport>;
  coatingInspectionReportEntries!: Table<DbReportEntry>;
  coatingLaboaratoryReportEntries!: Table<DbReportEntry>;
  chemicalManufacturerInspectionReports!: Table<DbReport>;
  chemicalManufacturerLaboratoryReports!: Table<DbReport>;
  chemicalManufacturerInspectionReportEntries!: Table<DbReportEntry>;
  chemicalManufacturerLaboratoryReportEntries!: Table<DbReportEntry>;
  powderNumberLabReports!: Table<DbReport>;
  powderNumberLabReportEntries!: Table<DbReportEntry>;
  powderManufacturerReports!: Table<DbReport>;
  powderManufacturerReportEntries!: Table<DbReportEntry>;
  syncQueue!: Table<SyncQueueItem>;
  constructor() {
    super('qualicoatWorkspace');
    this.version(1).stores({
      coatingInspections: 'id,offlineDate',
      coatingInspectionReports: 'id,offlineDate',
      coatingLaboratoryReports: 'id,offlineDate',
      coatingInspectionReportEntries: 'id,reportId,version,offlineDate',
      coatingLaboaratoryReportEntries: 'id,reportId,version,offlineDate',
    });

    // Version 2: Add sync queue for background uploads
    this.version(2).stores({
      coatingInspections: 'id,offlineDate',
      coatingInspectionReports: 'id,offlineDate',
      coatingLaboratoryReports: 'id,offlineDate',
      coatingInspectionReportEntries: 'id,reportId,version,offlineDate',
      coatingLaboaratoryReportEntries: 'id,reportId,version,offlineDate',
      syncQueue: 'id,status,type,priority,createdAt,[status+type],[status+priority]',
    });

    // Version 3: Add pre-treatment report tables
    this.version(3).stores({
      coatingInspections: 'id,offlineDate',
      coatingInspectionReports: 'id,offlineDate',
      coatingLaboratoryReports: 'id,offlineDate',
      coatingInspectionReportEntries: 'id,reportId,version,offlineDate',
      coatingLaboaratoryReportEntries: 'id,reportId,version,offlineDate',
      chemicalManufacturerInspectionReports: 'id,offlineDate',
      chemicalManufacturerLaboratoryReports: 'id,offlineDate',
      chemicalManufacturerInspectionReportEntries: 'id,reportId,version,offlineDate',
      chemicalManufacturerLaboratoryReportEntries: 'id,reportId,version,offlineDate',
      syncQueue: 'id,status,type,priority,createdAt,[status+type],[status+priority]',
    });

    // Version 4: Add powder number lab report tables
    this.version(4).stores({
      coatingInspections: 'id,offlineDate',
      coatingInspectionReports: 'id,offlineDate',
      coatingLaboratoryReports: 'id,offlineDate',
      coatingInspectionReportEntries: 'id,reportId,version,offlineDate',
      coatingLaboaratoryReportEntries: 'id,reportId,version,offlineDate',
      chemicalManufacturerInspectionReports: 'id,offlineDate',
      chemicalManufacturerLaboratoryReports: 'id,offlineDate',
      chemicalManufacturerInspectionReportEntries: 'id,reportId,version,offlineDate',
      chemicalManufacturerLaboratoryReportEntries: 'id,reportId,version,offlineDate',
      powderNumberLabReports: 'id,offlineDate',
      powderNumberLabReportEntries: 'id,reportId,version,offlineDate',
      syncQueue: 'id,status,type,priority,createdAt,[status+type],[status+priority]',
    });

    // Version 5: Add powder manufacturer report tables
    this.version(5).stores({
      coatingInspections: 'id,offlineDate',
      coatingInspectionReports: 'id,offlineDate',
      coatingLaboratoryReports: 'id,offlineDate',
      coatingInspectionReportEntries: 'id,reportId,version,offlineDate',
      coatingLaboaratoryReportEntries: 'id,reportId,version,offlineDate',
      chemicalManufacturerInspectionReports: 'id,offlineDate',
      chemicalManufacturerLaboratoryReports: 'id,offlineDate',
      chemicalManufacturerInspectionReportEntries: 'id,reportId,version,offlineDate',
      chemicalManufacturerLaboratoryReportEntries: 'id,reportId,version,offlineDate',
      powderNumberLabReports: 'id,offlineDate',
      powderNumberLabReportEntries: 'id,reportId,version,offlineDate',
      powderManufacturerReports: 'id,offlineDate',
      powderManufacturerReportEntries: 'id,reportId,version,offlineDate',
      syncQueue: 'id,status,type,priority,createdAt,[status+type],[status+priority]',
    });

    this.on('populate', () => this.populate());
  }

  async populate() {}
}

export const db = new AppDB();
